<template>
  <div :class="$options.name">
    <v-toolbar dark color="blue darken-3" elevation="0" flat>
      <v-btn
        v-permission="['tag-create']"
        class="mr-4 primary"
        :disabled="loading"
        @click="$dialog.show('tag-edit-dialog')"
      >
        Добавить
      </v-btn>

      <v-text-field
        v-model="query"
        class="ml-2"
        append-icon="search"
        label="Поиск по имени"
        single-line
        hide-details
        @input="doSearch"
      />
    </v-toolbar>

    <v-data-table
      hide-default-footer
      multi-sort
      no-data-text="Ничего нет"
      :items="formattedItems"
      :server-items-length="Infinity"
      :headers="headers"
      :loading="loading"
      @update:sort-by="onChangeSortBy"
      @update:sort-desc="onChangeSortDesc"
    >
      <template v-slot:item.actions="{ item }">
        <v-btn
          v-permission="['tag-create']"
          color="light"
          :class="{ w100: $vuetify.breakpoint.lgAndDown }"
          x-small
          @click="$dialog.show('tag-edit-dialog', { item })"
        >
          <v-icon left>edit</v-icon> Редактировать
        </v-btn>

        <v-btn
          v-permission="['tag-delete']"
          color="error"
          class="ml-2"
          :class="{ w100: $vuetify.breakpoint.lgAndDown }"
          x-small
          @click="onRemove(item)"
        >
          <v-icon left>delete</v-icon> Удалить
        </v-btn>
      </template>

      <template v-slot:footer>
        <v-pagination
          v-if="currentPage && lastPage"
          color="blue darken-3"
          class="pb-4 pt-4"
          :length="lastPage"
          :total-visible="8"
          :value="currentPage"
          :disabled="lastPage === 1 || loading"
          @input="onChangePage"
        />
      </template>
    </v-data-table>

    <tag-edit-dialog
      @onSubmit="onSubmit($event)"
      @onRemove="onRemove($event)"
      @close="$dialog.hide('tag-edit-dialog')"
    />

    <tag-delete-confirmation
      @confirmed="onRemoveConfirm($event)"
      @canceled="$dialog.hide('tag-delete-confirmation')"
    />

    <tag-edit-confirmation
      @confirmed="onSubmitConfirm($event)"
      @canceled="$dialog.hide('tag-edit-confirmation')"
    />
  </div>
</template>

<script>

import debounce from 'lodash/debounce';

import TagEditDialog from '@/views/references/tags/components/TagEditDialog.vue';
import TagDeleteConfirmation from '@/components/dialogs/confirmations/tags/TagDeleteConfirmation.vue';
import TagEditConfirmation from '@/components/dialogs/confirmations/tags/TagEditConfirmation.vue';
import { mapState, mapActions } from 'vuex';

const defaultHeaders = [
  { text: 'Имя', value: 'name' },
  {
    value: 'actions',
    sortable: false,
    width: '30%',
    align: 'right',
  },
];

export default {
  name: 'tags-table',

  components: { TagDeleteConfirmation, TagEditDialog, TagEditConfirmation },

  props: {
    headers: {
      type: Array,
      default: () => defaultHeaders,
    },

    items: {
      type: Array,
      default: () => [],
    },

    loading: {
      type: Boolean,
      default: false,
    },

    currentPage: {
      type: Number,
      default: null,
    },

    lastPage: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      query: '',
      sortBy: [],
      sortDesc: [],
    };
  },

  computed: {
    ...mapState('tags', ['single']),

    formattedItems() {
      return this.items;
    },
  },

  methods: {
    ...mapActions(['showNotify']),
    ...mapActions('tags', ['create', 'update', 'getItem', 'remove', 'removeSingle']),

    onChangeSortBy(value) {
      this.sortBy = value;

      this.onChangeSort();
    },

    onChangeSortDesc(value) {
      this.sortDesc = value;

      this.onChangeSort();
    },

    // eslint-disable-next-line func-names
    onChangeSort: debounce(function () {
      this.doReload(this.currentPage ?? 1, this.sortBy, this.sortDesc, this.query);
    }, 50),

    // eslint-disable-next-line func-names
    doSearch: debounce(function () {
      this.doReload(1, this.sortBy, this.sortDesc, this.query);
    }, 750),

    onChangePage(page) {
      this.doReload(page, this.sortBy, this.sortDesc, this.query);
    },

    doReload(page, sortBy, sortDesc, query) {
      this.$emit('onDoReload', {
        page,
        sortBy,
        sortDesc,
        query,
      });
    },

    async onSubmit(item) {
      if (!item.id) {
        await this.onSubmitConfirm(item);

        return;
      }

      this.$dialog.show('tag-edit-confirmation', { item });
    },

    async onSubmitConfirm(item) {
      try {
        if (item.id) await this.update(item);
        else await this.create({ name: item.name });

        this.showNotify({
          type: 'success',
          message: `Тег ${!item.id ? item.name : ''} успешно ${item.id ? 'изменен' : 'создан'}`,
        });

        this.$dialog.hide('tag-edit-dialog');

        await this.doReload(this.currentPage, this.sortBy, this.sortDesc, this.query);
      } catch (error) {
        this.showNotify({
          type: 'error',
          message: error.response.status === 409
            ? 'Тег с таким именем уже существует'
            : error.message,
        });
      } finally {
        this.$dialog.hide('tag-edit-confirmation');
      }
    },

    async onRemove(item) {
      this.$dialog.show('tag-delete-confirmation', { item });
    },

    async onRemoveConfirm(item) {
      try {
        await this.remove(item.id);

        this.showNotify({
          type: 'success',
          message: 'Тег успешно удален',
        });
      } catch (error) {
        this.showNotify({
          type: 'error',
          message: error.message,
        });
      } finally {
        this.$dialog.hide('tag-delete-confirmation');
        this.$dialog.hide('tag-edit-dialog');
      }
    },
  },
};

</script>
